import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient } from '@angular/common/http';
import { Subscription } from 'rxjs';

interface TranslationMap {
  [key: string]: string;
}

@Component({
  selector: 'app-portal-root',
  templateUrl: './portal-root.component.html',
  styleUrls: ['./portal-root.component.scss'],
})
export class PortalRootComponent implements OnInit, OnDestroy {
  public translations: TranslationMap = {};
  public translationsReady = false;
  private subscriptions = new Subscription();

  constructor(
    private translateService: TranslateService,
    private http: HttpClient
  ) {}

  ngOnInit(): void {
    this.loadApiTranslations();
    this.subscribeToTranslationChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private subscribeToTranslationChanges(): void {
    this.subscriptions.add(
      this.translateService.onLangChange.subscribe(() => {
        this.loadApiTranslations();
      })
    );
  }

  private loadApiTranslations(): void {
    const currentLang = this.translateService.currentLang || 'en-US';

    this.subscriptions.add(
      this.http.get<TranslationMap>('api/search-result/customization/translations', {
        headers: { 'accept-language': currentLang }
      }).subscribe({
        next: (apiTranslations) => {
          this.buildTranslations(apiTranslations);
        },
        error: () => {
          this.buildTranslations({});
        }
      })
    );
  }

  private buildTranslations(apiTranslations: TranslationMap): void {
    this.translations = {
      ...this.getPortalTranslations(),
      ...apiTranslations
    };

    this.translationsReady = true;
  }

  private getPortalTranslations(): TranslationMap {
    return {
      account: this.translateService.instant('portalAccount'),
      addAddress: this.translateService.instant('portalAddAddress'),
      addEmailAddress: this.translateService.instant('portalAddEmailAddress'),
      addHomeNumber: this.translateService.instant('portalAddHomeNumber'),
      addMobileNumber: this.translateService.instant('portalAddMobileNumber'),
      address: this.translateService.instant('portalAddress'),
      adultIcon: this.translateService.instant('portalAdultIcon'),
      billsFines: this.translateService.instant('portalBillsFines'),
      bookmarks: this.translateService.instant('portalBookmarks'),
      booksCheckedOut: this.translateService.instant('portalBooksCheckedOut'),
      booksOnHold: this.translateService.instant('portalBooksOnHold'),
      by: this.translateService.instant('portalBy'),
      cancel: this.translateService.instant('portalCancel'),
      save: this.translateService.instant('portalSave'),
      cardNumber: this.translateService.instant('portalCardNumber'),
      carrier: this.translateService.instant('portalCarrier'),
      changeDefaultCatalog: this.translateService.instant('portalChangeDefaultCatalog'),
      changePassword: this.translateService.instant('portalChangePassword'),
      checkHoldStatus: this.translateService.instant('portalCheckHoldStatus'),
      checkouts: this.translateService.instant('portalCheckouts'),
      checkoutsRequests: this.translateService.instant('portalCheckoutsRequests'),
      city: this.translateService.instant('portalCity'),
      confirmation: this.translateService.instant('portalConfirmation'),
      confirm: this.translateService.instant('portalConfirm'),
      connectAccount: this.translateService.instant('portalConnectAccount'),
      connectOtherAccounts: this.translateService.instant('portalConnectOtherAccounts'),
      connectedAccounts: this.translateService.instant('portalConnectedAccounts'),
      contentPreferences: this.translateService.instant('portalContentPreferences'),
      copyPassphrase: this.translateService.instant('portalCopyPassphrase'),
      create: this.translateService.instant('portalCreate'),
      delete: this.translateService.instant('portalDelete'),
      disconnectAccount: this.translateService.instant('portalDisconnectAccount'),
      displayPassphrase: this.translateService.instant('portalDisplayPassphrase'),
      editAccountInformation: this.translateService.instant('portalEditAccountInformation'),
      editDetails: this.translateService.instant('portalEditDetails'),
      email: this.translateService.instant('portalEmail'),
      emails: this.translateService.instant('portalEmails'),
      events: this.translateService.instant('portalEvents'),
      expiration: this.translateService.instant('portalExpiration'),
      expires: this.translateService.instant('portalExpires'),
      for: this.translateService.instant('portalFor'),
      fullName: this.translateService.instant('portalFullName'),
      generatePassphrase: this.translateService.instant('portalGeneratePassphrase'),
      getCode: this.translateService.instant('portalGetCode'),
      getPassphrase: this.translateService.instant('portalGetPassphrase'),
      getPhrase: this.translateService.instant('portalGetPhrase'),
      getSharePhrase: this.translateService.instant('portalGetSharePhrase'),
      goBack: this.translateService.instant('portalGoBack'),
      hidePassphrase: this.translateService.instant('portalHidePassphrase'),
      holds: this.translateService.instant('portalHolds'),
      homeLibrary: this.translateService.instant('portalHomeLibrary'),
      homePhone: this.translateService.instant('portalHomePhone'),
      homePhoneNumbers: this.translateService.instant('portalHomePhoneNumbers'),
      loading: this.translateService.instant('portalLoading'),
      mail: this.translateService.instant('portalMail'),
      manageBooks: this.translateService.instant('portalManageBooks'),
      manageContent: this.translateService.instant('portalManageContent'),
      managePreferences: this.translateService.instant('portalManagePreferences'),
      membershipExpiration: this.translateService.instant('portalMembershipExpiration'),
      minor: this.translateService.instant('portalMinor'),
      minorIcon: this.translateService.instant('portalMinorIcon'),
      mobileNumber: this.translateService.instant('portalMobileNumber'),
      mobilePhone: this.translateService.instant('portalMobilePhone'),
      mobilePhoneNumbers: this.translateService.instant('portalMobilePhoneNumbers'),
      name: this.translateService.instant('portalName'),
      navigationMenu: this.translateService.instant('portalNavigationMenu'),
      nickname: this.translateService.instant('portalNickname'),
      noPreferenceSelected: this.translateService.instant('portalNoPreferenceSelected'),
      overview: this.translateService.instant('portalOverview'),
      passcode: this.translateService.instant('portalPasscode'),
      passphrase: this.translateService.instant('portalPassphrase'),
      personalizeYourExperience: this.translateService.instant('portalPersonalizeYourExperience'),
      pickUpArea: this.translateService.instant('portalPickUpArea'),
      preferredContactMethod: this.translateService.instant('portalPreferredContactMethod'),
      preferredLocation: this.translateService.instant('portalPreferredLocation'),
      preferredPickupLocation: this.translateService.instant('portalPreferredPickupLocation'),
      preventMoreLinkRequests: this.translateService.instant('portalPreventMoreLinkRequests'),
      libraryInformation: this.translateService.instant('portalLibraryInformation'),
      addresses: this.translateService.instant('portalAddresses'),
      readingHistory: this.translateService.instant('portalReadingHistory'),
      readingHistoryDescriptionConfirmation: this.translateService.instant('portalReadingHistoryDescriptionConfirmation'),
      readingHistoryDescriptionSuccess: this.translateService.instant('portalReadingHistoryDescriptionSuccess'),
      recommendations: this.translateService.instant('portalRecommendations'),
      registeredLibraryBranch: this.translateService.instant('portalRegisteredLibraryBranch'),
      removeAddress: this.translateService.instant('portalRemoveAddress'),
      removeHomePhone: this.translateService.instant('portalRemoveHomePhone'),
      removeMobilePhone: this.translateService.instant('portalRemoveMobilePhone'),
      returnToCatalog: this.translateService.instant('portalReturnToCatalog'),
      roomBookings: this.translateService.instant('portalRoomBookings'),
      toChangeYour: this.translateService.instant('portalToChangeYour'),
      contactYourLibrary: this.translateService.instant('portalContactYourLibrary'),
      showingFor: this.translateService.instant('portalShowingFor'),
      showcases: this.translateService.instant('portalShowcases'),
      state: this.translateService.instant('portalState'),
      streetAddress: this.translateService.instant('portalStreetAddress'),
      streetAddressLine2: this.translateService.instant('portalStreetAddressLine2'),
      success: this.translateService.instant('portalSuccess'),
      twoWay: this.translateService.instant('portalTwoWay'),
      unknownAuthor: this.translateService.instant('portalUnknownAuthor'),
      usePhrase: this.translateService.instant('portalUsePhrase'),
      viewDetails: this.translateService.instant('portalViewDetails'),
      welcome: this.translateService.instant('portalWelcome'),
      whoWillThisCodeBeFor: this.translateService.instant('portalWhoWillThisCodeBeFor'),
      yourNextRead: this.translateService.instant('portalYourNextRead'),
      zipCode: this.translateService.instant('portalZipCode'),
      emailMaxRequired: this.translateService.instant('portalEmailMaxRequired'),
      emailMinRequired: this.translateService.instant('portalEmailMinRequired'),
      homeMobilePhoneListTotalMaxRequired: this.translateService.instant('portalHomeMobilePhoneListTotalMaxRequired'),
      homePhoneLength: this.translateService.instant('portalHomePhoneLength'),
      homePhoneListMaxRequired: this.translateService.instant('portalHomePhoneListMaxRequired'),
      homePhoneListMinRequired: this.translateService.instant('portalHomePhoneListMinRequired'),
      homePhoneRequired: this.translateService.instant('portalHomePhoneRequired'),
      mobilePhoneLength: this.translateService.instant('portalMobilePhoneLength'),
      mobilePhoneListMaxRequired: this.translateService.instant('portalMobilePhoneListMaxRequired'),
      mobilePhoneListMinRequired: this.translateService.instant('portalMobilePhoneListMinRequired'),
      mobilePhoneRequired: this.translateService.instant('portalMobilePhoneRequired'),
      required: this.translateService.instant('portalRequired'),
      requiredAddress: this.translateService.instant('portalRequiredAddress'),
      requiredCity: this.translateService.instant('portalRequiredCity'),
      requiredMinAddress: this.translateService.instant('portalRequiredMinAddress'),
      requiredSateCode: this.translateService.instant('portalRequiredSateCode'),
      requiredZipCode: this.translateService.instant('portalRequiredZipCode'),
      serverForbidden: this.translateService.instant('portalServerForbidden'),
      serverUnknownError: this.translateService.instant('portalServerUnknownError'),
      serverValidation: this.translateService.instant('portalServerValidation'),
      userSuccessMessage: this.translateService.instant('portalUserSuccessMessage'),
      'format-group.AUDIO_BOOK': this.translateService.instant('format-group.AUDIO_BOOK'),
      'format-group.AUDIO_CD': this.translateService.instant('format-group.AUDIO_CD'),
      'format-group.BLU_RAY': this.translateService.instant('format-group.BLU_RAY'),
      'format-group.BOOK': this.translateService.instant('format-group.BOOK'),
      'format-group.DVD': this.translateService.instant('format-group.DVD'),
      'format-group.E_AUDIO_BOOK': this.translateService.instant('format-group.E_AUDIO_BOOK'),
      'format-group.E_BOOK': this.translateService.instant('format-group.E_BOOK'),
      'format-group.LARGE_PRINT': this.translateService.instant('format-group.LARGE_PRINT'),
      'format-group.OTHER': this.translateService.instant('format-group.OTHER'),
      'format-group.STREAMING_VIDEO': this.translateService.instant('format-group.STREAMING_VIDEO'),
      dueDate: this.translateService.instant('dueDate'),
      checkedOutDate: this.translateService.instant('checkedOutDate'),
      checkoutOverdue: this.translateService.instant('checkoutOverdue', { value: 'value' }),
      checkoutRenewedTimes: this.translateService.instant('checkoutRenewedTimes', { value: 'value' }),
      currentBalance: this.translateService.instant('portalCurrentBalance'),
      totalFines: this.translateService.instant('portalTotalFines'),
      settleYour: this.translateService.instant('portalSettleYour'),
      payFine: this.translateService.instant('portalPayFine'),
      minimumPaymentNotice: this.translateService.instant('portalMinimumPaymentNotice'),
      transactionHistory: this.translateService.instant('portalTransactionHistory'),
      dateOfTransaction: this.translateService.instant('portalDateOfTransaction'),
      amountPaid: this.translateService.instant('portalAmountPaid'),
      noTransactionHistory: this.translateService.instant('portalNoTransactionHistory'),
      paymentSuccess: this.translateService.instant('portalPaymentSuccess'),
      paymentError: this.translateService.instant('portalPaymentError'),
      selection: this.translateService.instant('portalSelection'),
      selectAll: this.translateService.instant('portalSelectAll'),
      selectPayment: this.translateService.instant('portalSelectPayment'),
      feeReason: this.translateService.instant('portalFeeReason'),
      person: this.translateService.instant('portalPerson'),
      itemTitle: this.translateService.instant('portalItemTitle'),
      transactionDate: this.translateService.instant('portalTransactionDate'),
      amount: this.translateService.instant('portalAmount'),
      selectFee: this.translateService.instant('portalSelectFee'),
      fineLibraryCharge: this.translateService.instant('portalFineLibraryCharge'),
      billsAndFines: this.translateService.instant('portalBillsAndFines'),
      renew: this.translateService.instant('renew'),
      checkIn: this.translateService.instant('checkIn'),
      getItem: this.translateService.instant('getItem'),
      Renewing: this.translateService.instant('Renewing'),
      checkInPending: this.translateService.instant('checkInPending'),
      noItemsToRenew: this.translateService.instant('noItemsToRenew'),
      doYouWantToRenewOverdueItems: this.translateService.instant('doYouWantToRenewOverdueItems'),
      renewDueSoonItems: this.translateService.instant('renewDueSoonItems'),
      renewOverdueAndDueSoonItems: this.translateService.instant('renewOverdueAndDueSoonItems'),
      renewItemsDueInDays: this.translateService.instant('renewItemsDueInDays'),
      noThanks: this.translateService.instant('noThanks'),
      renewItems: this.translateService.instant('renewItems'),
      sortByDueDateAsc: this.translateService.instant('sortByDueDateAsc'),
      sortByDueDateDesc: this.translateService.instant('sortByDueDateDesc'),
      sortByTitleAscending: this.translateService.instant('sortByTitleAscending'),
      sortByTitleDescending: this.translateService.instant('sortByTitleDescending'),
      sortByCheckoutDateAscending: this.translateService.instant('sortByCheckoutDateAscending'),
      sortByCheckoutDateDescending: this.translateService.instant('sortByCheckoutDateDescending'),
    };
  }

}
